<template>
  <div class="container py-4">
    <div v-if="loading" class="text-center py-5">
      <Spinner />
      <p class="small text-muted mt-3">{{ $t("approval_video_mobile_loading") }}</p>
    </div>

    <div v-else-if="error" class="alert alert-danger">{{ error }}</div>

    <template v-else>
      <h5 class="fw-semibold mb-3">{{ title }}</h5>
      <p class="small text-muted mb-4">{{ $t("approval_video_mobile_hint") }}</p>

      <div class="mb-3">
        <label class="form-label">{{ $t("approval_video_select_video") }}</label>
        <input
          ref="fileInput"
          type="file"
          class="form-control"
          accept="video/*"
          capture="environment"
          :disabled="uploading"
          @change="onFileSelected"
        />
      </div>

      <video v-if="previewUrl" class="w-100 rounded mb-3" playsinline controls :src="previewUrl" />

      <div v-if="uploading" class="mb-3">
        <div class="progress">
          <div
            class="progress-bar bg-violet"
            role="progressbar"
            :style="{ width: uploadPercent + '%' }"
            :aria-valuenow="uploadPercent"
            aria-valuemin="0"
            aria-valuemax="100"
          >
            {{ uploadPercent }}%
          </div>
        </div>
      </div>

      <div v-if="done" class="alert alert-success">
        {{ $t("approval_video_mobile_done") }}
      </div>

      <button
        type="button"
        class="btn btn-violet text-uppercase w-100"
        :disabled="!selectedFile || uploading || done"
        @click="upload"
      >
        {{ $t("approval_video_mobile_send") }}
      </button>
    </template>
  </div>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import Spinner from "@/components/general/Spinner";
import { formatFilename } from "@/use/utilities/media/formatFilename";
import { showSession, assignUrl, completeSession } from "@/use/repositories/approvalVideos";

const MAX_VIDEO_MB = 50;
const MAX_VIDEO_DURATION_SECONDS = 30;
const VIDEO_METADATA_TIMEOUT_MS = 5000;

function uploadToS3(actionUrl, inputs, file, filename, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const formData = new FormData();
    Object.keys(inputs).forEach((key) => formData.append(key, inputs[key]));
    formData.append("file", file, filename);

    xhr.upload.addEventListener("progress", (e) => {
      if (e.lengthComputable && typeof onProgress === "function") {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    });

    xhr.addEventListener("load", () => {
      if (xhr.status === 204 || xhr.status === 200) {
        resolve();
        return;
      }
      reject(new Error(`Upload failed (${xhr.status})`));
    });

    xhr.addEventListener("error", () => reject(new Error("Network error")));

    xhr.open("POST", actionUrl);
    xhr.send(formData);
  });
}

function readVideoDuration(file) {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    const url = URL.createObjectURL(file);
    let timeoutId = null;
    let settled = false;
    const cleanup = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      URL.revokeObjectURL(url);
      video.onloadedmetadata = null;
      video.onerror = null;
      video.removeAttribute("src");
      video.load();
    };
    const settle = (callback, value) => {
      if (settled) return;
      settled = true;
      cleanup();
      callback(value);
    };

    video.preload = "metadata";
    video.onloadedmetadata = () => {
      const duration = video.duration;
      if (Number.isFinite(duration) && duration > 0) {
        settle(resolve, duration);
        return;
      }
      settle(reject, new Error("Invalid video duration"));
    };
    video.onerror = () => {
      settle(reject, new Error("Invalid video duration"));
    };
    timeoutId = setTimeout(() => {
      settle(reject, new Error("Video metadata timeout"));
    }, VIDEO_METADATA_TIMEOUT_MS);
    video.src = url;
  });
}

export default {
  name: "ExternalApprovalVideo",
  components: { Spinner },
  setup() {
    const route = useRoute();
    const i18n = useI18n();
    const sessionId = computed(() => route.params.id);
    const loading = ref(true);
    const error = ref("");
    const session = ref(null);
    const selectedFile = ref(null);
    const previewUrl = ref("");
    const uploading = ref(false);
    const uploadPercent = ref(0);
    const done = ref(false);
    const fileInput = ref(null);

    const title = computed(() => {
      const s = session.value;
      if (!s) return "";
      return (
        s.prescription_number_text ||
        s.number_text ||
        s.prescription?.number_text ||
        i18n.t("approval_video_mobile_title")
      );
    });

    const revokePreview = () => {
      if (previewUrl.value) {
        URL.revokeObjectURL(previewUrl.value);
        previewUrl.value = "";
      }
    };

    const loadSession = async () => {
      loading.value = true;
      error.value = "";
      try {
        const { session: data } = await showSession(sessionId.value, false);
        session.value = data;
      } catch (e) {
        error.value = i18n.t("approval_video_mobile_session_error");
        console.error(e);
      } finally {
        loading.value = false;
      }
    };

    onMounted(loadSession);

    onBeforeUnmount(revokePreview);

    const onFileSelected = async (ev) => {
      revokePreview();
      selectedFile.value = null;
      const file = ev.target.files?.[0];
      if (!file) return;

      if (!file.type.startsWith("video/")) {
        error.value = i18n.t("approval_video_mobile_not_video");
        return;
      }

      const sizeMb = file.size / (1024 * 1024);
      if (sizeMb > MAX_VIDEO_MB) {
        error.value = i18n.t("approval_video_mobile_too_large", { max: MAX_VIDEO_MB });
        return;
      }

      let duration = null;
      try {
        duration = await readVideoDuration(file);
      } catch (e) {
        console.error(e);
        error.value = i18n.t("approval_video_mobile_duration_unavailable");
        return;
      }

      if (duration > MAX_VIDEO_DURATION_SECONDS) {
        error.value = i18n.t("approval_video_mobile_too_long", {
          seconds: MAX_VIDEO_DURATION_SECONDS,
        });
        return;
      }

      error.value = "";
      selectedFile.value = file;
      previewUrl.value = URL.createObjectURL(file);
    };

    const upload = async () => {
      if (!selectedFile.value) return;
      uploading.value = true;
      uploadPercent.value = 0;
      error.value = "";

      const friendlyName = formatFilename(selectedFile.value);

      try {
        const { data } = await assignUrl(
          {
            approval_video_session_id: sessionId.value,
            filename: friendlyName,
          },
          false,
        );

        const inputs = data.inputs;
        const actionUrl = data.attributes?.action;
        if (!inputs || !actionUrl) {
          throw new Error("Invalid signed URL response");
        }

        await uploadToS3(actionUrl, inputs, selectedFile.value, friendlyName, (pct) => {
          uploadPercent.value = pct;
        });

        await completeSession(
          sessionId.value,
          {
            filename: friendlyName,
          },
          false,
        );

        done.value = true;
      } catch (e) {
        console.error(e);
        error.value =
          typeof e?.message === "string" ? e.message : i18n.t("approval_video_mobile_upload_error");
      } finally {
        uploading.value = false;
      }
    };

    return {
      loading,
      error,
      session,
      title,
      fileInput,
      selectedFile,
      previewUrl,
      uploading,
      uploadPercent,
      done,
      onFileSelected,
      upload,
    };
  },
};
</script>

